import { useState } from "react";
import api from "../lib/api";
import { ArrowRight, Sparkles } from "lucide-react";

const VIBES = [
    { value: "nature", label: "Nature" },
    { value: "chic", label: "Chic" },
    { value: "food", label: "Food" },
    { value: "calme", label: "Calme" },
    { value: "fete", label: "Fête" },
    { value: "culture", label: "Culture" },
];

const PROFILES = [
    { value: "couple", label: "Couple" },
    { value: "famille", label: "Famille" },
    { value: "solo", label: "Solo" },
    { value: "amis", label: "Amis" },
];

const BUDGETS = [
    { value: "doux", label: "Doux (sous 200€/j)" },
    { value: "confortable", label: "Confortable (200-400€/j)" },
    { value: "luxe", label: "Luxe (400€+/j)" },
];

const ORIGINS = ["Paris", "Londres", "Bruxelles", "Lille", "Amsterdam", "Autre"];
const SEASONS = ["printemps", "été", "automne", "hiver"];

export default function WeekendBuilder() {
    const [form, setForm] = useState({
        origin: "Paris",
        budget: "confortable",
        profile: "couple",
        vibes: ["chic", "food"],
        nights: 2,
        season: "automne",
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const toggleVibe = (v) => {
        setForm((f) => ({
            ...f,
            vibes: f.vibes.includes(v) ? f.vibes.filter((x) => x !== v) : [...f.vibes, v],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setResult(null);
        setLoading(true);
        try {
            const res = await api.post("/weekend-builder", form);
            setResult(res.data);
        } catch (e) {
            setError("Notre concierge est momentanément indisponible. Réessayez dans un instant.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pt-28" data-testid="weekend-builder-page">
            <section className="px-6 md:px-12 max-w-[1600px] mx-auto pt-16 md:pt-24 pb-16">
                <p className="cp-tiny text-cp-foret mb-8 flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.25} /> Concierge · Week-end sur mesure
                </p>
                <h1 className="cp-serif text-4xl sm:text-5xl md:text-[56px] lg:text-[72px] xl:text-[92px] leading-[1.02] text-cp-encre max-w-5xl tracking-[-0.025em]">
                    Dites-nous qui<br />
                    vous êtes, nous<br />
                    <em className="cp-display-italic text-cp-foret">dessinons</em> le reste.
                </h1>
                <p className="cp-serif italic text-xl md:text-2xl font-light text-cp-gris leading-relaxed mt-12 max-w-3xl">
                    Un concierge éditorial, animé par IA, pensé pour composer des itinéraires qui vous ressemblent <em>vraiment</em>.
                </p>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
                <form onSubmit={submit} className="lg:col-span-5 flex flex-col gap-10" data-testid="weekend-form">
                    <div>
                        <p className="cp-kicker text-cp-foret mb-4">01 — Vous venez de</p>
                        <div className="flex flex-wrap gap-2">
                            {ORIGINS.map((o) => (
                                <button type="button" key={o} onClick={() => setForm({ ...form, origin: o })}
                                    className={`cp-kicker text-[11px] px-4 py-2.5 border ${form.origin === o ? "border-cp-noir bg-cp-noir text-cp-ivoire" : "border-cp-border"}`}>
                                    {o}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="cp-kicker text-cp-foret mb-4">02 — Profil</p>
                        <div className="flex flex-wrap gap-2">
                            {PROFILES.map((p) => (
                                <button type="button" key={p.value} onClick={() => setForm({ ...form, profile: p.value })}
                                    className={`cp-kicker text-[11px] px-4 py-2.5 border ${form.profile === p.value ? "border-cp-noir bg-cp-noir text-cp-ivoire" : "border-cp-border"}`}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="cp-kicker text-cp-foret mb-4">03 — Budget</p>
                        <div className="flex flex-col gap-2">
                            {BUDGETS.map((b) => (
                                <button type="button" key={b.value} onClick={() => setForm({ ...form, budget: b.value })}
                                    className={`cp-kicker text-[11px] text-left px-4 py-3 border ${form.budget === b.value ? "border-cp-foret bg-cp-foret text-cp-ivoire" : "border-cp-border"}`}>
                                    {b.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="cp-kicker text-cp-foret mb-4">04 — Envies (multi-choix)</p>
                        <div className="flex flex-wrap gap-2">
                            {VIBES.map((v) => (
                                <button type="button" key={v.value} onClick={() => toggleVibe(v.value)}
                                    data-testid={`vibe-${v.value}`}
                                    className={`cp-kicker text-[11px] px-4 py-2.5 border ${form.vibes.includes(v.value) ? "border-cp-foret bg-cp-foret text-cp-ivoire" : "border-cp-border"}`}>
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <p className="cp-kicker text-cp-foret mb-4">Nuits</p>
                            <div className="flex gap-2">
                                {[1, 2, 3].map((n) => (
                                    <button type="button" key={n} onClick={() => setForm({ ...form, nights: n })}
                                        className={`cp-kicker text-[11px] w-12 h-12 border ${form.nights === n ? "border-cp-noir bg-cp-noir text-cp-ivoire" : "border-cp-border"}`}>
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="cp-kicker text-cp-foret mb-4">Saison</p>
                            <div className="flex flex-wrap gap-2">
                                {SEASONS.map((s) => (
                                    <button type="button" key={s} onClick={() => setForm({ ...form, season: s })}
                                        className={`cp-kicker text-[11px] px-3 py-2.5 border ${form.season === s ? "border-cp-foret bg-cp-foret text-cp-ivoire" : "border-cp-border"}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button type="submit" disabled={loading} className="cp-btn-primary mt-6" data-testid="weekend-submit">
                        {loading ? "Le concierge réfléchit…" : "Composer mon week-end"}
                        <ArrowRight size={14} strokeWidth={1.5} />
                    </button>
                    {error && <p className="text-red-600 text-sm">{error}</p>}
                </form>

                <aside className="lg:col-span-7">
                    {!result && !loading && (
                        <div className="border border-cp-border bg-cp-craie p-10 md:p-16 min-h-[400px] flex flex-col justify-center">
                            <p className="cp-kicker text-cp-foret mb-6">En attente de vos envies…</p>
                            <p className="cp-serif text-3xl md:text-4xl leading-tight text-cp-noir">
                                Remplissez le formulaire et notre concierge éditorial compose en quelques secondes un itinéraire pensé pour vous.
                            </p>
                        </div>
                    )}
                    {loading && (
                        <div className="border border-cp-border bg-cp-craie p-10 md:p-16 min-h-[400px] flex items-center justify-center">
                            <p className="cp-serif text-3xl text-cp-foret italic animate-pulse">Le concierge réfléchit…</p>
                        </div>
                    )}
                    {result && (
                        <div className="border border-cp-border p-10 md:p-12" data-testid="weekend-result">
                            <p className="cp-kicker text-cp-foret mb-6">Votre week-end</p>
                            {result.intro && <p className="cp-serif text-2xl md:text-3xl italic font-light leading-snug text-cp-noir mb-10">{result.intro}</p>}
                            {result.day_plans?.map((d) => (
                                <div key={d.day} className="mb-10 pb-8 border-b border-cp-border last:border-0">
                                    <p className="cp-kicker text-cp-bleu-gris mb-2">Jour {d.day}</p>
                                    <h3 className="cp-serif text-3xl md:text-4xl text-cp-noir mb-5 leading-tight">{d.title}</h3>
                                    {d.morning && <p className="text-cp-noir font-light mb-3"><strong className="cp-kicker text-[10px] text-cp-foret mr-2">Matin</strong>{d.morning}</p>}
                                    {d.afternoon && <p className="text-cp-noir font-light mb-3"><strong className="cp-kicker text-[10px] text-cp-foret mr-2">Après-midi</strong>{d.afternoon}</p>}
                                    {d.evening && <p className="text-cp-noir font-light mb-3"><strong className="cp-kicker text-[10px] text-cp-foret mr-2">Soir</strong>{d.evening}</p>}
                                    {d.highlights?.length > 0 && (
                                        <ul className="mt-4 flex flex-col gap-1.5">
                                            {d.highlights.map((h, i) => <li key={i} className="text-cp-gris text-sm italic">— {h}</li>)}
                                        </ul>
                                    )}
                                </div>
                            ))}
                            {result.recommended_stays?.length > 0 && (
                                <div className="mb-6">
                                    <p className="cp-kicker text-cp-foret mb-3">Où poser sa valise</p>
                                    <ul className="flex flex-col gap-2">{result.recommended_stays.map((s, i) => <li key={i} className="text-cp-noir font-light">· {s}</li>)}</ul>
                                </div>
                            )}
                            {result.recommended_tables?.length > 0 && (
                                <div className="mb-6">
                                    <p className="cp-kicker text-cp-foret mb-3">À table</p>
                                    <ul className="flex flex-col gap-2">{result.recommended_tables.map((s, i) => <li key={i} className="text-cp-noir font-light">· {s}</li>)}</ul>
                                </div>
                            )}
                            {result.soundtrack && (
                                <p className="cp-kicker text-cp-bleu-gris mt-6">Bande-son · {result.soundtrack}</p>
                            )}
                            {result.outro && (
                                <p className="cp-serif text-2xl italic text-cp-foret mt-10 leading-snug">{result.outro}</p>
                            )}
                            {result._error && (
                                <p className="text-sm text-red-600 mt-4">Erreur technique — merci de réessayer.</p>
                            )}
                        </div>
                    )}
                </aside>
            </section>
        </div>
    );
}
